import { createBrowserRouter, Navigate } from 'react-router';
import { AppBoot, RequireAuth, RequireAnonymous, RouteError } from './components/Guards.jsx';
import { AppShell } from './components/AppShell.jsx';
import {
  ChangePasswordPage,
  ForgotPasswordPage,
  LoginPage,
  PasswordLinkPage,
  SelectOutletPage,
} from './features/auth/AuthPages.jsx';
import { ActivatePage } from './features/device/ActivatePage.jsx';
import { PinLoginPage } from './features/device/PinLoginPage.jsx';
import { ImpersonatePage } from './features/auth/ImpersonatePage.jsx';
import { HomePage } from './features/home/HomePage.jsx';
import { PosPage } from './features/pos/PosPage.jsx';
import { BillsPage } from './features/bills/BillsPage.jsx';
import { PublicReceiptPage } from './features/receipt/PublicReceiptPage.jsx';

/** Code-split route: the module is fetched the first time someone opens that page. */
const page = (load, name) => async () => ({ Component: (await load())[name] });

export const router = createBrowserRouter([
  // Public e-receipt (signed link) — no app boot, no login.
  { path: '/r/:token', element: <PublicReceiptPage />, errorElement: <RouteError /> },
  // Customer token board on a TV (secret link from Settings → Offline & displays) — no login.
  {
    path: '/display/:outletId',
    lazy: page(() => import('./features/display/TokenBoardPage.jsx'), 'TokenBoardPage'),
    errorElement: <RouteError />,
  },
  // Self-order kiosk (device registered as a self-order kiosk) — no staff login.
  {
    path: '/self-order',
    lazy: page(() => import('./features/selforder/SelfOrderApp.jsx'), 'SelfOrderApp'),
    errorElement: <RouteError />,
  },
  // Campaign email unsubscribe link — no login.
  {
    path: '/u/:token',
    lazy: page(() => import('./features/receipt/UnsubscribePage.jsx'), 'UnsubscribePage'),
    errorElement: <RouteError />,
  },
  {
    element: <AppBoot />,
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireAnonymous />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
        ],
      },
      // PIN screen also serves fast user switching while someone is signed in.
      { path: '/pin', element: <PinLoginPage /> },
      { path: '/activate', element: <ActivatePage /> },
      // Admin portal "View as client" (read-only support session).
      { path: '/impersonate', element: <ImpersonatePage /> },
      { path: '/reset-password', element: <PasswordLinkPage mode="reset" /> },
      { path: '/set-password', element: <PasswordLinkPage mode="set" /> },
      {
        element: <RequireAuth bare />,
        children: [
          { path: '/change-password', element: <ChangePasswordPage /> },
          { path: '/select-outlet', element: <SelectOutletPage /> },
        ],
      },
      {
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <HomePage /> },
          { path: 'pos', element: <PosPage /> },
          { path: 'bills', element: <BillsPage /> },
          { path: 'menu', lazy: page(() => import('./features/menu/MenuPage.jsx'), 'MenuPage') },
          { path: 'stock', lazy: page(() => import('./features/menu/StockPage.jsx'), 'StockPage') },
          { path: 'settings', lazy: page(() => import('./features/settings/SettingsPage.jsx'), 'SettingsPage') },
          { path: 'staff', lazy: page(() => import('./features/staff/UsersPage.jsx'), 'UsersPage') },
          { path: 'roles', lazy: page(() => import('./features/staff/RolesPage.jsx'), 'RolesPage') },
          { path: 'devices', lazy: page(() => import('./features/devices/DevicesPage.jsx'), 'DevicesPage') },
          { path: 'printers', lazy: page(() => import('./features/printers/PrintersPage.jsx'), 'PrintersPage') },
          { path: 'inventory', lazy: page(() => import('./features/inventory/InventoryPage.jsx'), 'InventoryPage') },
          { path: 'cash', lazy: page(() => import('./features/cash/CashPage.jsx'), 'CashPage') },
          { path: 'outlets', lazy: page(() => import('./features/outlets/OutletsPage.jsx'), 'OutletsPage') },
          { path: 'kds', lazy: page(() => import('./features/kds/KdsPage.jsx'), 'KdsPage') },
          { path: 'reports', lazy: page(() => import('./features/reports/ReportsPage.jsx'), 'ReportsPage') },
          { path: 'reports/:id', lazy: page(() => import('./features/reports/ReportsPage.jsx'), 'ReportsPage') },
          { path: 'customers', lazy: page(() => import('./features/customers/CustomersPage.jsx'), 'CustomersPage') },
          {
            path: 'customers/:id',
            lazy: page(() => import('./features/customers/CustomersPage.jsx'), 'CustomersPage'),
          },
          { path: 'account', element: <ChangePasswordPage embedded /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
